import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isQueryMountedWithData } from '@/hooks/api/sse/utils.ts';
import { EXTRACT_ENTITY_FIELDS_KEY } from '@/utils/queryKeys.ts';
import { ExtractEntityField, ExtractEntityFields } from '@/types/extract.ts';

const useInvalidateExtractEntityField = (automationId: string) => {
  const queryClient = useQueryClient();

  return useCallback(
    (incomingFieldRef: ExtractEntityField, extractEntityId: string): unknown[][] => {
      const queryKey = EXTRACT_ENTITY_FIELDS_KEY(automationId, extractEntityId);

      const queryIsMountedWithData = isQueryMountedWithData(queryClient, queryKey);
      if (!queryIsMountedWithData) {
        return [queryKey];
      }

      queryClient.setQueryData<ExtractEntityFields>(queryKey, (data) => {
        const currentResults = data?.results ?? [];

        const updatedResults = currentResults.filter((item) => {
          const sameReferenceId = item.id === incomingFieldRef.id;
          const sameReferenceIndex = item.index === incomingFieldRef.index;
          return !sameReferenceId && !sameReferenceIndex;
        });

        updatedResults.push(incomingFieldRef);
        updatedResults.sort((a, b) => a.index - b.index);

        return { results: updatedResults };
      });

      return [];
    },
    [automationId, queryClient],
  );
};

export default useInvalidateExtractEntityField;
